import { UserLanguage } from '../../../../features/account/types/User'
import { InternalFeatureMap } from '../../../api/features'
import { LanguageMaps } from '../../../api/language'
import { ContentLanguageTranslation } from '../../../types/InternalAnalystComment'
import { InternalKeyword, KeywordFeature, KeywordMutation, KeywordPriority } from '../types/InternalKeyword'

export const keywordLanguages: UserLanguage[] = ['en', 'ja', 'zh']

export const keywordPriorities = [KeywordPriority.Normal, KeywordPriority.High]

export const keywordPriorityLabelKeys: Record<KeywordPriority, string> = {
  [KeywordPriority.Normal]: 'internal-service-management:keywords_priority_normal',
  [KeywordPriority.High]: 'internal-service-management:keywords_priority_high',
}

export const emptyKeyword: KeywordMutation = {
  name: '',
  type: 'feature',
  linkedIds: [],
  priority: KeywordPriority.Normal,
  names: null,
  translations: null,
}

export type KeywordRow = {
  keyword: InternalKeyword
  names: ContentLanguageTranslation
  linkedFeatureNames: string[]
}

export type KeywordFormValues = {
  names: ContentLanguageTranslation
  priority: KeywordPriority
  linkedFeatures: KeywordFeature[]
}

// Plain code-unit comparison, as lodash `orderBy` in the legacy app, keeps the feature order analysts are used to.
export const getActiveFeatures = (featureMap: InternalFeatureMap | undefined): KeywordFeature[] =>
  Object.values(featureMap ?? {})
    .filter((feature) => feature.active)
    .map(({ legacyId, name }) => ({ legacyId, name }))
    .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))

const getKeywordNames = (keyword: InternalKeyword, languageMaps: LanguageMaps | undefined): ContentLanguageTranslation =>
  keywordLanguages.reduce<ContentLanguageTranslation>(
    (names, language) => ({
      ...names,
      [language]: languageMaps?.[language]?.keywords?.[keyword.id]?.name ?? (language === 'en' ? keyword.name : ''),
    }),
    {}
  )

export const buildKeywordRows = (keywords: InternalKeyword[], languageMaps: LanguageMaps | undefined, activeFeatures: KeywordFeature[]): KeywordRow[] =>
  keywords.map((keyword) => ({
    keyword,
    names: getKeywordNames(keyword, languageMaps),
    linkedFeatureNames: activeFeatures.filter((feature) => keyword.linkedIds.includes(feature.legacyId)).map((feature) => feature.name),
  }))

export const toKeywordDraft = (row: KeywordRow): KeywordMutation => ({ ...row.keyword, names: row.names })

export const toKeywordClone = (row: KeywordRow): KeywordMutation => {
  const { id, createdAt, modifiedAt, createdBy, modifiedBy, ...clone } = toKeywordDraft(row)
  return clone
}

export const toKeywordFormValues = (keyword: KeywordMutation, activeFeatures: KeywordFeature[]): KeywordFormValues => ({
  names: keywordLanguages.reduce<ContentLanguageTranslation>((names, language) => ({ ...names, [language]: keyword.names?.[language] ?? '' }), {}),
  priority: keyword.priority,
  linkedFeatures: activeFeatures.filter((feature) => keyword.linkedIds.includes(feature.legacyId)),
})

// Links to inactive features can't be selected in the dialog but must survive a save, and the legacy app sends the ids sorted.
export const toKeywordMutation = (keyword: KeywordMutation, values: KeywordFormValues, activeFeatures: KeywordFeature[]): KeywordMutation => {
  const activeIds = new Set(activeFeatures.map((feature) => feature.legacyId))
  const inactiveLinkedIds = keyword.linkedIds.filter((linkedId) => !activeIds.has(linkedId))
  const linkedIds = Array.from(new Set([...inactiveLinkedIds, ...values.linkedFeatures.map((feature) => feature.legacyId)])).sort((a, b) => a - b)

  return { ...keyword, name: values.names.en ?? '', names: values.names, priority: values.priority, linkedIds }
}
